import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { clampPosition, initialPosition, MARGEN, panelMaxHeight, panelWidth } from './floating-panel-position';

/**
 * Ventana flotante **sin fondo bloqueante**: se mueve arrastrando su cabecera y la pantalla de
 * detrás sigue visible y utilizable, para poder leer la fila del listado mientras se rellena.
 *
 * No es un `Dialog` modal a propósito: en este caso lo que se pide es mirar el pedido mientras se
 * escribe el motivo, y un fondo oscuro lo impide. Se pinta en un portal sobre `document.body` para
 * que ninguna animación ni `transform` de un contenedor desplace el `fixed`.
 */
export function FloatingPanel({ open, onClose, title, description, children, footer, width = 420 }: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [posicion, setPosicion] = useState<{ x: number; y: number } | null>(null);
  const arrastre = useRef<{ dx: number; dy: number } | null>(null);
  const focoPrevio = useRef<HTMLElement | null>(null);
  const idDescripcion = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const medir = useCallback(() => panelRef.current?.offsetHeight ?? 0, []);
  const ancho = panelWidth(width, typeof window === 'undefined' ? width : window.innerWidth);

  // Antes de pintar: se guarda quién tenía el foco y se coloca, para no ver un salto desde la
  // esquina al sitio definitivo. El orden importa: si se enfoca un campo antes de guardar, lo que
  // se guarda es el campo del propio panel y al cerrar el foco acaba en `body`.
  useLayoutEffect(() => {
    if (!open) { setPosicion(null); return; }
    focoPrevio.current = document.activeElement as HTMLElement | null;
    setPosicion(initialPosition({
      width, height: medir() || 220,
      viewportWidth: window.innerWidth, viewportHeight: window.innerHeight
    }));
  }, [open, width, medir]);

  // Ya colocado, se lleva el foco al campo marcado **una sola vez por apertura**. No se usa
  // `autoFocus` de React porque lo aplica antes que nada y rompe la devolución del foco al cerrar.
  // La dependencia es «está colocado» y no la posición: si dependiera de la posición, arrastrar la
  // ventana volvería a robar el foco al usuario, que puede estar escribiendo en la página de detrás.
  const colocado = posicion !== null;
  useLayoutEffect(() => {
    if (!open || !colocado) return;
    panelRef.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus();
  }, [open, colocado]);

  // Si el contenido cambia de alto (un textarea que se estira), se vuelve a meter dentro.
  useEffect(() => {
    if (!open || !panelRef.current) return;
    const observador = new ResizeObserver(() => {
      const altura = medir();
      setPosicion((actual) => {
        if (!actual) return actual;
        const siguiente = clampPosition({
          x: actual.x, y: actual.y, width, height: altura,
          viewportWidth: window.innerWidth, viewportHeight: window.innerHeight
        });
        // Sin cambio real no se re-renderiza: el observador dispara en cada ajuste de contenido.
        return siguiente.x === actual.x && siguiente.y === actual.y ? actual : siguiente;
      });
    });
    observador.observe(panelRef.current);
    return () => observador.disconnect();
  }, [open, width, medir]);

  useEffect(() => {
    if (!open) return;
    const alRedimensionar = () => setPosicion((actual) => {
      if (!actual) return actual;
      const siguiente = clampPosition({
        x: actual.x, y: actual.y, width,
        height: medir() || 220,
        viewportWidth: window.innerWidth, viewportHeight: window.innerHeight
      });
      return siguiente.x === actual.x && siguiente.y === actual.y ? actual : siguiente;
    });
    window.addEventListener('resize', alRedimensionar);
    return () => window.removeEventListener('resize', alRedimensionar);
  }, [open, width, medir]);

  // Escape cierra, como en cualquier diálogo, pero solo si el foco está dentro: la página de
  // detrás sigue viva y no se le roba el teclado.
  useEffect(() => {
    if (!open) return;
    const alPulsar = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && panelRef.current?.contains(document.activeElement)) {
        onCloseRef.current();
      }
    };
    window.addEventListener('keydown', alPulsar);
    return () => window.removeEventListener('keydown', alPulsar);
  }, [open]);

  // Al cerrar, el foco vuelve a donde estaba: quien navega con teclado no pierde su fila. Solo si
  // ese elemento sigue en la página.
  useEffect(() => {
    if (!open) return;
    return () => {
      const anterior = focoPrevio.current;
      focoPrevio.current = null;
      // `isConnected` basta: al correr la limpieza el panel ya está desmontado, así que un
      // elemento del propio panel nunca llega aquí vivo.
      if (!anterior?.isConnected) return;
      anterior.focus?.();
    };
  }, [open]);

  const empezarArrastre = (event: React.PointerEvent) => {
    if (!posicion || event.button !== 0) return;
    // La X de cerrar vive en la cabecera: pulsarla no debe arrastrar la ventana.
    if ((event.target as HTMLElement).closest('button')) return;
    arrastre.current = { dx: event.clientX - posicion.x, dy: event.clientY - posicion.y };
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
  };

  const arrastrando = (event: React.PointerEvent) => {
    if (!arrastre.current) return;
    setPosicion(clampPosition({
      x: event.clientX - arrastre.current.dx,
      y: event.clientY - arrastre.current.dy,
      width,
      height: medir() || 220,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight
    }));
  };

  const soltar = () => { arrastre.current = null; };

  if (!open) return null;

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-label={title}
      aria-describedby={description ? idDescripcion : undefined}
      className="fixed z-50 flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xl"
      style={{
        left: posicion?.x ?? MARGEN,
        top: posicion?.y ?? MARGEN,
        width: ancho,
        maxWidth: `calc(100vw - ${MARGEN * 2}px)`,
        maxHeight: panelMaxHeight(typeof window === 'undefined' ? 600 : window.innerHeight),
        // Mientras no se ha medido queda transparente y sin clics: así no se ve el salto desde la
        // esquina. No se usa `visibility: hidden` porque un campo oculto así **no recibe el foco**
        // y el campo marcado con `data-autofocus` no se podría enfocar al abrir.
        opacity: posicion ? 1 : 0,
        pointerEvents: posicion ? 'auto' : 'none'
      }}
      onPointerMove={arrastrando}
      onPointerUp={soltar}
      onPointerCancel={soltar}
    >
      <div
        className="flex shrink-0 cursor-move touch-none items-start justify-between gap-2 border-b border-border px-3 py-2 select-none"
        onPointerDown={empezarArrastre}
        title="Arrastra para mover"
      >
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{title}</p>
          {description ? <p id={idDescripcion} className="text-[11px] text-muted-foreground">{description}</p> : null}
        </div>
        <Button variant="ghost" size="icon-sm" onClick={onClose} title="Cerrar" aria-label="Cerrar">
          <X />
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-3">{children}</div>
      {footer ? (
        <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-border px-3 py-2">{footer}</div>
      ) : null}
    </div>,
    document.body
  );
}
